import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Button, 
  Menu,
  MenuItem,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  Link
} from '@mui/material';
import { Menu as MenuIcon, Settings as SettingsIcon, Feedback as FeedbackIcon, Tune as TuneIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import DatabaseStatusIndicator from './common/DatabaseStatusIndicator';
import { WorkflowProvider } from '../context/WorkflowContext';
import { DebugProvider } from './DebugPanel';

const Layout: React.FC = () => {
  const { logout, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    handleCloseMobileMenu();
    logout();
  };
  
  // Mobile menu handlers
  const handleOpenMobileMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  
  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigateToManage = () => {
    navigate('/user-management');
    handleCloseMobileMenu();
  };

  const handleNavigateToAppSettings = () => {
    navigate('/application-settings');
    handleCloseMobileMenu();
  };

  return (
    <DebugProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar 
          position="static" 
          sx={{ 
            background: 'linear-gradient(135deg, #1a1a1a 0%, #000000 100%)', // Subtle dark gradient
            position: 'relative',
            zIndex: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}
        >
        <Toolbar sx={{ minHeight: '64px !important', height: '64px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, position: 'relative' }}>
            <Box 
              sx={{ 
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                mr: 3,
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              <img 
                src="/TotalSERP-logo_b_and_w.png" 
                alt="TotalSERP Logo" 
                height={220} 
                style={{ 
                  filter: 'brightness(0) invert(1)'
                }} 
              />
            </Box>
          </Box>

         {/* Right navbar menu - Responsive */}
          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleOpenMobileMenu}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleCloseMobileMenu}
                PaperProps={{
                  elevation: 3,
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={() => { window.open('https://wkf.ms/4qAO87X', '_blank', 'noopener,noreferrer'); handleCloseMobileMenu(); }}>
                  <ListItemText primary="Submit Feedback" />
                </MenuItem>
                {isAdmin && (
                  <>
                    <MenuItem onClick={handleNavigateToManage}>
                      <ListItemText primary="User Management" />
                    </MenuItem>
                    <MenuItem onClick={handleNavigateToAppSettings}>
                      <ListItemText primary="App Settings" />
                    </MenuItem>
                  </>
                )}
                {user && (
                  <MenuItem disabled>
                    <ListItemText primary={`Welcome, ${user.displayName}`} />
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button 
                color="inherit" 
                startIcon={<FeedbackIcon />}
                onClick={() => window.open('https://wkf.ms/4qAO87X', '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none' }}
              >
                Submit Feedback
              </Button>
              {isAdmin && (
                <>
                  <Button 
                    color="inherit" 
                    startIcon={<SettingsIcon />}
                    onClick={handleNavigateToManage}
                    sx={{ textTransform: 'none' }}
                  >
                    User Management
                  </Button>
                  <Button 
                    color="inherit" 
                    startIcon={<TuneIcon />}
                    onClick={handleNavigateToAppSettings}
                    sx={{ textTransform: 'none' }}
                  >
                    App Settings
                  </Button>
                </>
              )}
              {user && (
                <Typography variant="body2" sx={{ mr: 2 }}>
                  Welcome, {user.displayName}
                </Typography>
              )}
              <Button color="inherit" onClick={handleLogout}>
                Logout
              </Button>
            </Box>
          )}

        </Toolbar>
      </AppBar>
      
      <Container 
        component="main" 
        maxWidth={false}
        sx={{ 
          flexGrow: 1, 
          py: 3,
          backgroundColor: theme => theme.palette.grey[100],
          zIndex: 2
        }}
      >
        <WorkflowProvider>
          <Outlet />
        </WorkflowProvider>
      </Container>
      
      <Box
        component="footer"
        sx={{
          py: 1,
          px: 2,
          mt: 'auto',
          backgroundColor: theme => theme.palette.grey[200],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        {/* Left side - empty for balance */}
        <Box sx={{ minWidth: '150px' }} />

        {/* Center - Copyright */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} TotalSERP
          </Typography>
        </Box>

        {/* Right side - Debug info for developers */}
        <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
          <DatabaseStatusIndicator />
        </Box>
      </Box>
      </Box>
    </DebugProvider>
  );
};

export default Layout;